// src/features/streams/components/StreamControlsSection.tsx

import { Stack, Select, Switch, NumberInput, Textarea, Divider, Alert } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { useController, type Control, type UseFormWatch } from 'react-hook-form';
import {
  EDITABLE_RECORD_MODES,
  RECORD_MODE_LABELS,
  EDITABLE_OFFLINE_MODES,
  OFFLINE_DISPLAY_MODE_LABELS,
  STREAM_CONSTRAINTS,
  type StreamRecordMode,
  type StreamOfflineDisplayMode,
} from '@/lib/constants/stream';
import { CAPTION_LANGUAGES, CAPTION_POSITION_LABELS, CAPTION_POSITIONS } from '@/lib/constants/captions';
import { useCaptionsEligibility } from '@/features/captions/useCaptionsEligibility';
import type { StreamOverviewFormData } from '../schemas/StreamForm.schema';
import type { StreamViewData } from '@/lib/types/Stream';

interface StreamControlsSectionProps {
  control: Control<StreamOverviewFormData>;
  watch: UseFormWatch<StreamOverviewFormData>;
  stream: StreamViewData;
  /**
   * HTTP-proxy stream (customer HLS relayed through nginx, no Wowza path):
   * recording and automated captions can't work, so those controls are hidden.
   * Their form fields ride along untouched — hide-only, no schema change.
   */
  isHttpProxy?: boolean;
}

export function StreamControlsSection({
  control,
  watch,
  stream,
  isHttpProxy = false,
}: StreamControlsSectionProps) {
  // --- Field controllers ---

  const { field: recordModeField } = useController({ control, name: 'recordMode' });
  const { field: recordOverrideField } = useController({ control, name: 'recordOverride' });
  const { field: recordAppendTimeoutField } = useController({ control, name: 'recordAppendTimeout' });
  const { field: hasEmbeddedCaptionsField } = useController({ control, name: 'hasEmbeddedCaptions' });
  const { field: automatedCaptionsEnabledField } = useController({ control, name: 'automatedCaptionsEnabled' });
  const { field: automatedCaptionLanguageField } = useController({ control, name: 'automatedCaptionLanguage' });
  const { field: automatedCaptionPositionField } = useController({ control, name: 'automatedCaptionPosition' });
  const { field: offlineDisplayModeField } = useController({ control, name: 'offlineDisplayMode' });
  const { field: offlineMsgHtmlField, fieldState: offlineMsgHtmlState } = useController({ control, name: 'offlineMsgHtml' });

  // Live automated captions: entitlement-gated (this hook is the launch gate) —
  // unentitled accounts never see the fields, and the form fields ride along
  // untouched (defaults mirror server state).
  const { eligible: captionsEligible } = useCaptionsEligibility();

  const watchedRecordMode = watch('recordMode');
  const watchedOfflineDisplayMode = watch('offlineDisplayMode');
  const watchedAutomatedCaptionsEnabled = watch('automatedCaptionsEnabled');
  const offlineMsgLength = watch('offlineMsgHtml')?.length ?? 0;

  // Original stream values for read-only detection
  const originalOfflineMode = stream.stream?.offline?.offlineDisplayMode ?? 'default_message';

  // Advanced offline modes are read-only (offline_poster, media, list)
  const isReadOnlyOfflineMode = !EDITABLE_OFFLINE_MODES.includes(originalOfflineMode as StreamOfflineDisplayMode);

  // Build record mode options
  const recordModeOptions = EDITABLE_RECORD_MODES.map((mode) => ({
    value: mode,
    label: RECORD_MODE_LABELS[mode],
  }));

  // Build offline mode options
  const offlineModeOptions = EDITABLE_OFFLINE_MODES.map((mode) => ({
    value: mode,
    label: OFFLINE_DISPLAY_MODE_LABELS[mode],
  }));

  return (
    <Stack gap="md">
      {/* HTTP-proxy streams: explain the reduced control set once, up front */}
      {isHttpProxy && (
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          This stream relays your HLS source through an HTTP proxy. Live status
          detection, recording, automated captions, and destinations are not
          available for this stream type.
        </Alert>
      )}

      {/* Recording controls — Wowza-side; never shown for HTTP-proxy streams.
          Hidden fields ride along untouched (same pattern as schedule mode). */}
      {!isHttpProxy && (
        <>
          {/* Recording Mode */}
          <Select
            label="Recording Mode"
            description={
              watchedRecordMode === 'schedule'
                ? 'Manage recording times in the Recording Schedule section below.'
                : undefined
            }
            data={recordModeOptions}
            value={recordModeField.value}
            onChange={(val) => {
              if (!val) return;
              recordModeField.onChange(val);
              // When switching to automatic, force recordOverride off (legacy behavior)
              if (val === 'automatic') {
                recordOverrideField.onChange(false);
              }
            }}
            onBlur={recordModeField.onBlur}
            allowDeselect={false}
          />

          {/* Recording On/Off — only visible in manual mode */}
          {watchedRecordMode === 'manual' && (
            <Switch
              label={recordOverrideField.value ? 'Recording On' : 'Recording Off'}
              checked={recordOverrideField.value}
              onChange={(event) => recordOverrideField.onChange(event.currentTarget.checked)}
              onBlur={recordOverrideField.onBlur}
              color={recordOverrideField.value ? 'green' : 'gray'}
              size="md"
            />
          )}

          {/* New Recording Wait Time — hidden in schedule mode (legacy rule); value carries through */}
          {watchedRecordMode !== 'schedule' && (
            <NumberInput
              label="New Recording Wait Time"
              description="Seconds to wait after encoder disconnects before starting a new recording"
              value={recordAppendTimeoutField.value}
              onChange={(val) => {
                if (typeof val === 'number') {
                  recordAppendTimeoutField.onChange(val);
                }
              }}
              onBlur={recordAppendTimeoutField.onBlur}
              suffix=" seconds"
              min={STREAM_CONSTRAINTS.RECORD_APPEND_TIMEOUT_MIN}
              max={STREAM_CONSTRAINTS.RECORD_APPEND_TIMEOUT_MAX}
            />
          )}

          <Divider />
        </>
      )}

      {/* Embedded Captions */}
      <Switch
        label="Use Embedded Stream Captions"
        checked={hasEmbeddedCaptionsField.value}
        onChange={(event) => hasEmbeddedCaptionsField.onChange(event.currentTarget.checked)}
        onBlur={hasEmbeddedCaptionsField.onBlur}
        size="md"
      />

      {/* Automated Captions (live AI captioning) — entitled accounts only;
          runs on the Wowza path, so never offered for HTTP-proxy streams */}
      {captionsEligible && !isHttpProxy && (
        <>
          <Switch
            label="Automated Captions"
            description="Live AI captions. Billed per streamed hour."
            checked={automatedCaptionsEnabledField.value}
            onChange={(event) => automatedCaptionsEnabledField.onChange(event.currentTarget.checked)}
            onBlur={automatedCaptionsEnabledField.onBlur}
            size="md"
          />
          <Select
            label="Caption language"
            data={CAPTION_LANGUAGES.map((l) => ({ value: l.value, label: l.label }))}
            value={automatedCaptionLanguageField.value}
            onChange={(val) => {
              if (val) automatedCaptionLanguageField.onChange(val);
            }}
            onBlur={automatedCaptionLanguageField.onBlur}
            disabled={!watchedAutomatedCaptionsEnabled}
            allowDeselect={false}
            maw={280}
          />
          <Select
            label="Caption position"
            description="Choose Top for streams with a scorebug or lower-third graphics."
            data={CAPTION_POSITIONS.map((p) => ({ value: p, label: CAPTION_POSITION_LABELS[p] }))}
            value={automatedCaptionPositionField.value}
            onChange={(val) => {
              if (val) automatedCaptionPositionField.onChange(val);
            }}
            onBlur={automatedCaptionPositionField.onBlur}
            disabled={!watchedAutomatedCaptionsEnabled}
            allowDeselect={false}
            maw={280}
          />
        </>
      )}

      <Divider />

      {/* Offline Display Mode */}
      {isReadOnlyOfflineMode && (
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          This offline mode ({OFFLINE_DISPLAY_MODE_LABELS[originalOfflineMode as StreamOfflineDisplayMode]}) is configured but editing is not yet available.
        </Alert>
      )}
      <Select
        label="Offline Display Mode"
        data={offlineModeOptions}
        value={offlineDisplayModeField.value}
        onChange={(val) => {
          if (val) offlineDisplayModeField.onChange(val);
        }}
        onBlur={offlineDisplayModeField.onBlur}
        disabled={isReadOnlyOfflineMode}
        allowDeselect={false}
      />

      {/* Offline Message — visible only for custom_message mode */}
      {watchedOfflineDisplayMode === 'custom_message' && (
        <Textarea
          label="Offline Message"
          description={`Custom message shown when stream is offline. Supports text or HTML. ${offlineMsgLength}/${STREAM_CONSTRAINTS.OFFLINE_MSG_MAX}`}
          value={offlineMsgHtmlField.value}
          onChange={offlineMsgHtmlField.onChange}
          onBlur={offlineMsgHtmlField.onBlur}
          error={offlineMsgHtmlState.error?.message}
          maxLength={STREAM_CONSTRAINTS.OFFLINE_MSG_MAX}
          rows={4}
        />
      )}
    </Stack>
  );
}
